import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Video, CalendarClock, Check, ExternalLink } from 'lucide-react';
import { calendarReps, calendarAvailability, calendarBook } from '../lib/marketingApi';
import { formatApiError } from '../lib/auth';

const TZ_OPTIONS = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Australia/Sydney', 'UTC',
];
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; } };
const todayStr = () => new Date().toISOString().slice(0, 10);

// Book a Microsoft Teams call on a connected rep's calendar, in the lead's timezone.
export default function BookTeamsCall({ lead, canEdit, onBooked }) {
  const [reps, setReps] = useState([]);
  const [loadingReps, setLoadingReps] = useState(true);
  const [rep, setRep] = useState('');
  const [date, setDate] = useState(todayStr());
  const [tz, setTz] = useState(browserTz());
  const [duration, setDuration] = useState(30);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [picked, setPicked] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(lead.last_meeting || null);

  useEffect(() => {
    calendarReps().then((d) => {
      setReps(d.reps || []);
      const me = (d.reps || []).find((r) => r.is_me);
      setRep((me || (d.reps || [])[0] || {}).crm_user_id || '');
    }).catch(() => {}).finally(() => setLoadingReps(false));
  }, []);

  useEffect(() => {
    const opts = TZ_OPTIONS.includes(tz) ? TZ_OPTIONS : [tz, ...TZ_OPTIONS];
    if (!opts.includes(tz)) setTz(opts[0]);
  }, [tz]);

  const loadSlots = async () => {
    if (!rep) { toast.error('Pick a connected rep first'); return; }
    setLoadingSlots(true); setSlots(null); setPicked(null);
    try {
      const d = await calendarAvailability({ rep_user_id: rep, date, tz, duration });
      setSlots(d.slots || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Could not load availability')); }
    finally { setLoadingSlots(false); }
  };

  const book = async () => {
    if (!picked) return;
    setBooking(true);
    try {
      const d = await calendarBook({
        rep_user_id: rep, start: picked.start, end: picked.end, tz, duration,
        subject: `BillPortal — call with ${lead.name || lead.email}`,
        attendee_email: lead.email, attendee_name: lead.name || undefined,
        body: `<p>Looking forward to walking you through BillPortal.</p>`,
        lead_id: lead.id,
      });
      setBooked({ start: picked.start, tz, join_url: d.join_url });
      setSlots(null); setPicked(null);
      toast.success('Teams call booked and invite sent');
      onBooked && onBooked();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Booking failed')); }
    finally { setBooking(false); }
  };

  const tzOpts = TZ_OPTIONS.includes(tz) ? TZ_OPTIONS : [tz, ...TZ_OPTIONS];
  const input = 'h-9 w-full rounded-md border border-slate-200 px-2.5 text-sm outline-none focus:border-[#0035A8]';

  if (loadingReps) return <div className="p-5"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /></div>;

  if (reps.length === 0) {
    return (
      <div className="p-5 text-xs text-slate-500" data-testid="book-teams-empty">
        No rep has connected a Microsoft 365 calendar yet. Connect yours in <span className="font-semibold">Settings → My Calendar</span>.
      </div>
    );
  }

  return (
    <div className="space-y-3 p-5" data-testid="book-teams-call">
      {booked && (
        <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="book-teams-confirmed">
          Teams call booked for {booked.start} ({booked.tz}).
          {booked.join_url && <a href={booked.join_url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-1 font-semibold underline">Join link <ExternalLink className="h-3 w-3" /></a>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rep calendar</label>
          <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid="book-rep-select" className={input}>
            {reps.map((r) => <option key={r.crm_user_id} value={r.crm_user_id}>{r.name}{r.is_me ? ' (You)' : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</label>
          <input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" className={input} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Duration</label>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="book-duration" className={input}>
            <option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>60 min</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Lead timezone</label>
          <select value={tz} onChange={(e) => setTz(e.target.value)} data-testid="book-tz" className={input}>
            {tzOpts.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
      </div>

      <button onClick={loadSlots} disabled={loadingSlots || !canEdit} data-testid="book-load-slots"
        className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-[#0035A8] text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8] hover:text-white disabled:opacity-60">
        {loadingSlots ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Show open slots
      </button>

      {slots !== null && (
        slots.length === 0 ? (
          <p className="text-center text-xs text-slate-400" data-testid="book-no-slots">No open slots that day (9am–5pm). Try another date.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5" data-testid="book-slots">
            {slots.map((s) => (
              <button key={s.start} onClick={() => setPicked(s)} data-testid={`book-slot-${s.start}`}
                className={`rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${picked?.start === s.start ? 'border-[#0035A8] bg-[#0035A8] text-white' : 'border-slate-200 text-slate-700 hover:border-[#0035A8]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        )
      )}

      {picked && canEdit && (
        <button onClick={book} disabled={booking} data-testid="book-confirm"
          className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
          {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Book Teams call at {picked.label}
        </button>
      )}
      {!canEdit && <p className="text-xs text-slate-400">Read-only — ask an editor to book calls.</p>}
    </div>
  );
}
